<template>

    <ui-view class="view-action-form" v-spinner="loading" :hide="loading">

        <ui-heading :title="pageTitle">
            <ui-button color="positive" :loading="submitting" @click="$refs.form.submit()">Opslaan</ui-button>
            <ui-button
                color="medium"
                type="bordered"
                :to="backRoute"
                slot="left">Annuleren</ui-button>
        </ui-heading>

        <ui-form-panel auto-validate ref="form" @submit="submit">

            <ui-form-date-input
                title="Datum"
                name="date"
                rules="required"
                v-model="pendingSowing.date"/>

            <ui-form-radio
                title="Zaad type"
                name="seedType"
                rules="required"
                :options="seedTypeOptions"
                v-model="pendingSowing.seedType"/>

            <ui-form-radio
                title="Oorsprong locatie"
                name="originLocation"
                rules="required"
                :options="originLocationOptions"
                v-model="pendingSowing.originLocation"/>

            <ui-form-radio
                title="Oorsprong type"
                name="originType"
                rules="required"
                :options="originTypeOptions"
                v-model="pendingSowing.originType"/>

            <ui-form-input
                title="Visgebied"
                name="fishingArea"
                v-model="pendingSowing.fishingArea"/>

            <ui-form-input
                title="MZI gebied"
                name="mziArea"
                v-model="pendingSowing.mziArea"/>

            <ui-form-input
                number
                title="MZI: Aantal uur gevist"
                name="duration"
                suffix="Uur"
                v-model="pendingSowing.duration"/>

            <ui-form-dropdown
                title="Herkomst jaar"
                name="seedYear"
                :options="seedYearOptions"
                rules="required"
                v-model="pendingSowing.seedYear"/>

            <ui-form-dropdown
                searchable
                title="Schip"
                name="shipId"
                :options="shipOptions"
                empty
                v-model="pendingSowing.shipId"/>

            <ui-form-input
                number
                title="Bruto"
                name="brutoWeight"
                suffix="mTon"
                v-model="pendingSowing.brutoWeight"/>

            <ui-form-input
                number
                title="Netto"
                name="nettoWeight"
                suffix="mTon"
                v-model="pendingSowing.nettoWeight"/>

            <ui-form-input
                decimal
                title="Tarra"
                name="tarra"
                suffix="%"
                v-model="pendingSowing.tarra"/>

            <ui-form-input
                number
                title="Bustal"
                name="bustal"
                v-model="pendingSowing.bustal"/>

            <ui-form-input
                number
                title="Stukstal"
                name="stukstal"
                v-model="pendingSowing.stukstal"/>

            <ui-form-textarea
                title="Bijzonderheden"
                name="notes"
                v-model="pendingSowing.notes"/>

        </ui-form-panel>

    </ui-view>

</template>

<script>

import Vue from 'vue';
import moment from 'moment';
import Component from 'vue-class-component';
import { clone, pick, range } from 'es-toolkit';

import createQuery from 'js-core/js/apollo/queries/createQuery';

import SeedType from 'common/enums/SeedType';
import OriginType from 'common/enums/OriginType';
import OriginLocation from 'common/enums/OriginLocation';
import actionTypeTitleFilter from 'common/filters/actionTypeTitle';

import AllShipsQuery from 'gql/queries/SowingForm/AllShips.gql';
import FindSowingQuery from 'gql/queries/SowingForm/FindSowing.gql';
import AllParcelsQuery from 'gql/queries/SowingForm/AllParcels.gql';

import SowingMutations from 'app/mutations/SowingMutations';

@Component({

    name: 'SowingForm',
    apollo: {

        sowing: createQuery(FindSowingQuery, {

            skip() {
                return !this.sowingId;
            },

            variables() {

                return {
                    id: this.sowingId
                };
            },

            response(response) {

                this.pendingSowing = pick(response, Object.keys(this.pendingSowing));
                this.pendingSowing.date = moment(this.pendingSowing.date).format('YYYY-MM-DD');
            }
        }),

        parcels: createQuery(AllParcelsQuery),
        ships: createQuery(AllShipsQuery)
    }
})
export default class SowingForm extends Vue
{
    loading = 0;
    submitting = false;

    pendingSowing = {
        date: null,
        shipId: null,
        seedType: null,
        originLocation: null,
        originType: null,
        fishingArea: null,
        mziArea: null,
        seedYear: null,
        brutoWeight: null,
        nettoWeight: null,
        tarra: null,
        bustal: null,
        stukstal: null,
        notes: null,
        duration: null
    };

    breadcrumb(){

        return [{
            title: 'Vak detail',
            route: this.backRoute
        }, this.pageTitle];
    }

    async submit() {

        this.submitting = true;
        let input = clone(this.pendingSowing);

        try {

            input.id = this.sowingId;

            await SowingMutations.update(input);

            this.logger.info('Sowing updated');
            this.$toast.success(`De ${this.actionType} is aangepast`);

            this.$router.replace(this.backRoute);
        }
        catch(e) {

            this.logger.error('Error updating sowing', e);
            this.$toast.error(`De ${this.actionType} kon niet worden opgeslagen`);
        }

        this.submitting = false;
    }

    get pageTitle(){

        return this.actionType ? `${this.actionType} wijzigen` : null;
    }

    get actionType(){

        return this.sowing ? actionTypeTitleFilter(this.sowing.__typename, this.sowing) : null;
    }

    get sowingId(){

        return this.$route.params.id;
    }

    get backRoute(){

        if(!this.sowing){
            return { name: 'sections'};
        }

        return {
            name: 'section-detail',
            params: {
                id: this.sowing.cohort.sectionId
            }
        };
    }

    get seedTypeOptions(){

        return SeedType.options();
    }

    get originLocationOptions(){

        return OriginLocation.options();
    }

    get originTypeOptions(){

        return OriginType.options();
    }

    get seedYearOptions(){

        const currentYear = new Date().getFullYear();

        return range(10)
            .map(offset => currentYear - offset)
            .map(year => ({
                label: year,
                value: year.toString()
            }));
    }

    get parcelOptions(){

        if(!this.parcels){
            return [];
        }

        return this.parcels.items.map(parcel => ({
            label: parcel.alias || parcel.parcelLocation.name,
            value: parcel.id
        }));
    }

    get shipOptions(){

        if(!this.ships){
            return [];
        }

        return this.ships.items.map(ship => ({
            label: ship.name,
            value: ship.id
        }));
    }
}

</script>